import React, { useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import i18n from 'i18n-js';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import {
    InputAdornment,
    IconButton,
    Box,
    FormGroup,
    FormControlLabel,
    Switch,
    Typography,
} from '@material-ui/core';

import { Input } from 'components/shared';
import { images } from 'assets';
import { Positions, MenuType } from 'constants/enums';
import { useStyles } from '../../../styles';
import { useAppSelector } from 'index';
import CodeScanner from 'components/shared/components/CodeScanner';

export interface AdvancedTabProps {
    disabled: boolean;
    isFieldDisabled: (name: string, additionalOption?: boolean) => boolean;
}

export default function AdvancedTab({ disabled, isFieldDisabled }: AdvancedTabProps) {
    const classes = useStyles();
    const { values, errors, setFieldValue } = useFormikContext<any>();
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
    const user = useAppSelector((state) => state.user);
    const menu = useAppSelector((state) => state.menuDetails.menu);
    const currentRestaurant = user.restaurants?.find((x) => x.id === user.currentRestaurantId);
    const currentPosition = currentRestaurant?.currentPosition;
    const isCatalog = menu.type === MenuType.Catalog;
    const isCatalogManager =
        (menu.type === MenuType.Catalog &&
            currentPosition === Positions.MANAGER &&
            menu.catalogData?.allowToEditCatalog) ??
        false;

    const scanTries = useRef<null | ReturnType<typeof setTimeout>>(null);
    const [showScanner, setShowScanner] = useState(false);

    function handleScannerClose() {
        setShowScanner(false);
        if (scanTries.current) {
            clearTimeout(scanTries.current);
        }
    }

    function handleScannerOpen() {
        setShowScanner(true);
        scanTries.current = setTimeout(() => handleScannerClose(), 10000) as ReturnType<
            typeof setTimeout
        >;
    }

    return (
        <>
            {restaurant.coolBoxData.isCoolBox && (
                <>
                    <Box component="h6" className={classes.dishFormSectionTitle}>
                        {i18n.t('form.supplier')}
                    </Box>
                    <Box
                        display="flex"
                        flexDirection="row"
                        width="100%"
                        justifyContent="space-between"
                    >
                        <Box display="flex" flexDirection="column" width="48%">
                            {restaurant.coolBoxData.isCoolBox && (
                                <Input
                                    disabled={isFieldDisabled('supplierName')}
                                    variant="filled"
                                    fullWidth={true}
                                    name="supplierName"
                                    type="text"
                                    label={i18n.t('form.supplierName')}
                                    error={errors.supplierName}
                                    placeholder={i18n.t('form.supplierName')}
                                    style={{ marginTop: 0, marginBottom: 0 }}
                                />
                            )}
                        </Box>
                        <Box display="flex" flexDirection="column" width="48%">
                            {restaurant.coolBoxData.isCoolBox && (
                                <Input
                                    disabled={isFieldDisabled('externalId')}
                                    variant="filled"
                                    fullWidth={true}
                                    name="externalId"
                                    type="text"
                                    label={i18n.t('form.externalId')}
                                    error={errors.externalId}
                                    placeholder={i18n.t('form.externalId')}
                                    style={{ marginTop: 0, marginBottom: 0 }}
                                />
                            )}
                        </Box>
                    </Box>
                </>
            )}

            <Box component="h6" className={classes.dishFormSectionTitle}>
                {i18n.t('form.identification')}
            </Box>
            <Box display="flex" flexDirection="row" width="100%" justifyContent="space-between">
                <Box display="flex" flexDirection="column" width="48%">
                    <div className={classes.flexRowSpaceBetween}>
                        <Input
                            disabled={isFieldDisabled('barcode')}
                            variant="filled"
                            fullWidth={true}
                            name="barcode"
                            type="text"
                            label={i18n.t('form.barcode')}
                            error={errors.barcode}
                            placeholder={i18n.t('form.barcode')}
                            style={{ marginTop: 0, marginBottom: 0 }}
                            InputProps={{
                                endAdornment: ((!isCatalog && !disabled) ||
                                    (isCatalog && isCatalogManager)) && (
                                    <InputAdornment position="end" onClick={handleScannerOpen}>
                                        <IconButton>
                                            <img
                                                className={classes.barcodeIcon}
                                                src={images.icons.barcodeScanIcon}
                                                alt="barcode-scanner-icon"
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </div>
                </Box>
                <Box display="flex" flexDirection="column" width="48%">
                    <Input
                        disabled={isFieldDisabled('primeCost')}
                        variant="filled"
                        fullWidth={false}
                        name="primeCost"
                        type="text"
                        label={i18n.t('form.primeCost')}
                        error={errors.primeCost}
                        placeholder={i18n.t('form.primeCost')}
                        style={{ marginTop: 0, marginBottom: 0 }}
                    />
                </Box>
            </Box>

            <Box component="h6" className={classes.dishFormSectionTitle}>
                {i18n.t('form.stockManagement')}
            </Box>

            {!restaurant.coolBoxData.isCoolBox && currentPosition === Positions.MANAGER && (
                <FormGroup>
                    <FormControlLabel
                        control={
                            <Switch
                                color="primary"
                                name="enablePrintingActions"
                                checked={values.stockQuantity !== null}
                                onChange={() => {
                                    if (values.stockQuantity === null) {
                                        setFieldValue('stockQuantity', 0);
                                    } else {
                                        setFieldValue('stockQuantity', null);
                                    }
                                }}
                            />
                        }
                        label={i18n.t('common.trackStockQuantity')}
                    />
                </FormGroup>
            )}

            <Typography variant="body1" component="p" className={classes.stockToggleDescription}>
                {values.stockQuantity === null
                    ? i18n.t('common.itemWillBeAlwaysVisible')
                    : i18n.t('common.zeroStockWarning')}
            </Typography>

            {values.stockQuantity !== null && (
                <Box display="flex" flexDirection="row" width="100%" justifyContent="space-between">
                    <Box display="flex" flexDirection="column" width="48%">
                        <Input
                            disabled={isFieldDisabled('stockQuantity')}
                            variant="filled"
                            fullWidth={true}
                            name="stockQuantity"
                            type="text"
                            label={i18n.t('form.stockQuantity')}
                            error={errors.stockQuantity}
                            placeholder={i18n.t('form.stockQuantity')}
                        />
                    </Box>
                    <Box display="flex" flexDirection="column" width="48%">
                        <Input
                            disabled={isFieldDisabled('minimumStock')}
                            variant="filled"
                            fullWidth={true}
                            name="minimumStock"
                            type="text"
                            label={i18n.t('form.minimumStock')}
                            error={errors.minimumStock}
                            placeholder={i18n.t('form.minimumStock')}
                        />
                    </Box>
                </Box>
            )}

            <Dialog open={showScanner} onClose={handleScannerClose}>
                <DialogContent>
                    <CodeScanner
                        onScan={(code: string) => {
                            setFieldValue('barcode', code);
                            handleScannerClose();
                        }}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
}
